/**
 * Core-100 R4 — CORE-Y: the mark beside a Create Ticket field that an extraction (an uploaded repair order or a
 * conversation) proposed — with its confidence — or looked for and could not find. Low and unknown confidence never
 * look like high (different words AND a different tone). A field no extraction touched shows nothing.
 */
import { cn } from '@/lib/utils';
import { fieldMark, fieldMarkText, type ExtractionProvenance, type ProvenanceField } from '@/lib/intake/extractionProvenance';

export function ExtractedFieldMark({
  provenance,
  field,
  value,
}: {
  provenance: ExtractionProvenance | null | undefined;
  field: ProvenanceField;
  value: unknown;
}) {
  const mark = fieldMark(provenance, field, value);
  if (!mark) return null;
  const tone =
    mark.kind === 'not_found'
      ? 'border-dashed text-muted-foreground'
      : mark.kind === 'edited'
        ? 'text-muted-foreground'
        : mark.confidence === 'high'
          ? 'border-emerald-600/40 text-emerald-700 dark:text-emerald-400'
          : mark.confidence === 'medium'
            ? 'border-amber-600/50 text-amber-700 dark:text-amber-400'
            : 'border-destructive/60 text-destructive font-medium';
  return (
    <span
      className={cn('ml-2 inline-flex items-center rounded border px-1.5 py-0 text-[10px] leading-4 align-middle', tone)}
      data-testid={`extracted-field-mark-${field}`}
      data-mark-kind={mark.kind}
      data-mark-confidence={mark.kind === 'extracted' ? mark.confidence : undefined}
    >
      {fieldMarkText(mark)}
    </span>
  );
}
